import { api, type FetchOptions, type MutOpts } from "@/api"
import { queryOptions, useMutation, useQuery } from "@tanstack/react-query"

import { queryClient } from "@/lib/query-client"

import { patientKeys } from "../patients/queries"

const deviceKeys = {
  all: ["devices"] as const,
  list: () => [...deviceKeys.all, "list"] as const,
  lists: <TQuery>(query?: TQuery) => [...deviceKeys.list(), { query }] as const,
  create: () => [...deviceKeys.list(), "create"] as const,
  update: () => [...deviceKeys.list(), "update"] as const,
}

export const devicesQueryOptions = (
  fetchOpts?: FetchOptions<"get", "/devices/">
) => {
  const query = fetchOpts?.params?.query

  return queryOptions({
    queryKey: deviceKeys.lists({ query }),
    queryFn: () =>
      api.GET("/devices/", { params: { query } }).then(({ data }) => data),
    staleTime: 30_000,
  })
}

export const useDeviceTypesQuery = (
  fetchOpts?: FetchOptions<"get", "/device-types/">
) => {
  const query = fetchOpts?.params?.query

  return useQuery({
    queryKey: deviceKeys.lists(query),
    queryFn: () => {
      return api
        .GET("/device-types/", { params: { query } })
        .then(({ data }) => data)
    },
  })
}

export const useDeviceModelsQuery = (
  fetchOpts?: FetchOptions<"get", "/device-models/">,
  queryOpts?: { enabled?: boolean }
) => {
  const query = fetchOpts?.params?.query

  return useQuery({
    queryKey: deviceKeys.lists(query),
    queryFn: () => {
      return api
        .GET("/device-models/", { params: { query } })
        .then(({ data }) => data)
    },
    ...queryOpts,
  })
}

export const useCreateDeviceMutation = (
  mutationOptions?: MutOpts<"post", "/devices/">
) => {
  return useMutation({
    mutationKey: deviceKeys.create(),
    mutationFn: fetchOpts => {
      return api.POST("/devices/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: deviceKeys.list(),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useUpdateDeviceMutation = (
  mutationOptions?: MutOpts<"patch", "/devices/{id}/">
) => {
  return useMutation({
    mutationKey: deviceKeys.update(),
    mutationFn: fetchOpts => {
      return api.PATCH("/devices/{id}/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: deviceKeys.list(),
      })
      queryClient.invalidateQueries({
        queryKey: patientKeys.list(),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useDeleteDeviceMutation = (
  mutationOptions?: MutOpts<"delete", "/devices/{id}/">
) => {
  return useMutation({
    mutationKey: deviceKeys.update(),
    mutationFn: fetchOpts => {
      return api.DELETE("/devices/{id}/", fetchOpts)
    },

    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: deviceKeys.list(),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useSetDeviceWifiCredentialsMutation = (
  mutationOptions?: MutOpts<"post", "/devices/{id}/set-wifi">
) => {
  return useMutation({
    mutationKey: deviceKeys.update(),
    mutationFn: fetchOpts => {
      return api.POST("/devices/{id}/set-wifi", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}
